/**
 * Pronunciation Readings: See How the Other Language Is Said (feature guide)
 *
 * Screenshots were taken from Sokuji v0.45.4, the first release with readings.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "Pronunciation Readings: See How the Other Language Is Said",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "Readings show how the other side’s language is pronounced, as small text above the words: pinyin over Chinese, kana over Japanese kanji, romanization over Korean, and Latin letters over Russian and Ukrainian. They help when you read a translation aloud, or follow the other side, without knowing how the words sound. Readings are only drawn on screen: they are never sent to the AI provider, and the transcript, exports and copied text stay as they were."
  },
  "steps": [
    {
      "title": "Turn readings on",
      "content": "Click the gear in the toolbar above the conversation to open the display settings. When your target language has readings, the panel has a switch that names it, here <strong>Show Japanese readings (kana)</strong>. Turn it on.",
      "screenshot": "01-switch.png"
    },
    {
      "title": "Which lines get readings",
      "content": "Readings go on every line in your target language: your translations, and the other side’s own words when they speak it. Lines in other languages stay as they are. Here Chinese is translated into Japanese: my translations get kana, and so does what the other side says in Japanese. In a face-to-face conversation, each line follows its own language.",
      "screenshot": "02-japanese.png"
    },
    {
      "title": "Chinese: pinyin",
      "content": "Each character gets its pinyin, with tone marks. A character with more than one reading is read in context (还 hái, 重新 chóng, 银行 háng), and the tone changes of 一 and 不 are shown as they are spoken (一趟 yí tàng).",
      "screenshot": "03-chinese.png"
    },
    {
      "title": "Korean: romanization",
      "content": "Each word gets its Revised Romanization, written the way the word sounds rather than letter by letter: 같이 gachi, 국물이 gungmuri, 신라호텔 sillahotel.",
      "screenshot": "04-korean.png"
    },
    {
      "title": "Russian and Ukrainian: Latin letters",
      "content": "Each word is spelled in Latin letters: Russian in the BGN/PCGN system (Здравствуйте zdravstvuyte, всё vsyo), Ukrainian in the national system of 2010 (Київ kyiv). The letters follow the spelling, which is not always how a word sounds.",
      "screenshot": "05-russian.png"
    },
    {
      "title": "Japanese: kana and the dictionary",
      "content": "Kana go over the kanji only; hiragana and katakana are left as they are. Japanese readings need a 17 MB dictionary, which downloads the first time you turn them on and is kept for later. The panel shows the download while it runs. If it fails, or Japanese readings stop working, the panel says so and offers <strong>Retry</strong>.",
      "screenshot": "06-download.png"
    },
    {
      "title": "In the subtitle view",
      "content": "In the desktop app, the subtitle view has its own display settings, with its own readings switch, independent of the conversation’s. Readings show while the subtitles are expanded, not in the compact view. The browser extension shows readings in its side panel, not in the subtitles it draws over the meeting page."
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "The switch appears only when your target language has readings: Chinese (Simplified), Japanese, Korean, Russian or Ukrainian. Traditional Chinese and Cantonese have none yet.",
      "With a two-way target such as Chinese ⇄ English, the Chinese half gets readings.",
      "While a line is still being spoken, its last word gets its reading once the line is finished.",
      "Chinese, Korean, Russian and Ukrainian readings need no download."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "Why is a Japanese reading wrong?",
        "answer": "The dictionary gives each word one reading and cannot tell from the meaning which one is meant. 空いている reads あいている (vacant) even in 電車が空いている, where すいている (not crowded) is meant, and 辛い reads つらい (painful) even for a spicy curry, where からい is meant. Numbers with counters (一人, 3日, 4月1日) and お腹が空いた are handled. A word newer than the dictionary, such as 令和, can also be misread."
      },
      {
        "question": "Can I get romaji for Japanese?",
        "answer": "Not yet. Japanese shows kana for now."
      },
      {
        "question": "Do readings need the internet?",
        "answer": "Readings are worked out on your computer. The only download is the Japanese dictionary, once."
      },
      {
        "question": "Do they use much memory?",
        "answer": "Japanese readings use about 170 MB while they are on, and turning the switch off frees it. The other languages use very little."
      },
      {
        "question": "Does clearing my downloaded models remove the dictionary?",
        "answer": "Yes. It downloads again the next time Japanese readings are on."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "读音标注：看懂对方语言怎么读",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "读音标注会在文字上方用小字显示对方语言的读法：中文标拼音，日语汉字标假名，韩语标罗马字，俄语和乌克兰语标拉丁字母。当你要把译文读出来、或者想跟上对方说的话，却不知道这些字怎么读时，它能帮上忙。读音只显示在屏幕上：不会发送给 AI 服务商，对话记录、导出和复制的文字都保持原样。"
  },
  "steps": [
    {
      "title": "打开读音标注",
      "content": "点击对话上方工具栏中的齿轮，打开显示设置。如果你的目标语言有读音，面板里会出现一个写明语言的开关，这里是<strong>显示日语读音（假名）</strong>。把它打开即可。",
      "screenshot": "01-switch.png"
    },
    {
      "title": "哪些行会标读音",
      "content": "所有使用目标语言的行都会标读音：你的译文，以及对方用这种语言说的原话。其他语言的行保持原样。这里是中文译成日语：我的译文标上了假名，对方说的日语也一样。面对面对话时，每一行按它自己的语言来标。",
      "screenshot": "02-japanese.png"
    },
    {
      "title": "中文：拼音",
      "content": "每个字都标上带声调的拼音。多音字按上下文来读（还 hái、重新 chóng、银行 háng），一和不的变调按实际读法标出（一趟 yí tàng）。",
      "screenshot": "03-chinese.png"
    },
    {
      "title": "韩语：罗马字",
      "content": "每个词标上韩国国语罗马字（Revised Romanization），按实际发音而不是逐个字母来写：같이 gachi、국물이 gungmuri、신라호텔 sillahotel。",
      "screenshot": "04-korean.png"
    },
    {
      "title": "俄语和乌克兰语：拉丁字母",
      "content": "每个词用拉丁字母拼写：俄语采用 BGN/PCGN 方案（Здравствуйте zdravstvuyte、всё vsyo），乌克兰语采用 2010 年的国家标准（Київ kyiv）。拉丁字母对应的是拼写，不一定就是实际读音。",
      "screenshot": "05-russian.png"
    },
    {
      "title": "日语：假名与词典",
      "content": "假名只标在汉字上，平假名和片假名保持原样。日语读音需要一个 17 MB 的词典，第一次打开时会自动下载，之后一直保留。下载期间面板会显示进度。如果下载失败，或者日语读音无法使用，面板会提示并提供<strong>重试</strong>按钮。",
      "screenshot": "06-download.png"
    },
    {
      "title": "在字幕视图中",
      "content": "在桌面应用中，字幕视图有自己的显示设置和读音开关，与对话的设置互不影响。读音在字幕展开时显示，紧凑视图中不显示。浏览器扩展在侧边栏中显示读音，覆盖在会议页面上的字幕不显示读音。"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "只有目标语言有读音时才会出现这个开关：简体中文、日语、韩语、俄语或乌克兰语。繁体中文和粤语暂时没有。",
      "目标语言是中文 ⇄ 英语这类双向组合时，中文那一半会标读音。",
      "一行话还在说的时候，最后一个词的读音要等这一行说完才出现。",
      "中文、韩语、俄语和乌克兰语的读音不需要下载。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "为什么日语读音有时不对？",
        "answer": "词典给每个词只有一种读法，无法按意思判断该用哪一种。例如 電車が空いている 里的 空いている 应读 すいている（不拥挤），却会标成 あいている（空着）；形容咖喱的 辛い 应读 からい，却会标成 つらい（难受）。数字加量词（一人、3日、4月1日）和 お腹が空いた 已经处理。比词典更新的词（例如 令和）也可能读错。"
      },
      {
        "question": "日语可以显示罗马字吗？",
        "answer": "暂时还不行，日语目前只显示假名。"
      },
      {
        "question": "读音需要联网吗？",
        "answer": "读音在你的电脑上生成。唯一需要下载的是日语词典，而且只下载一次。"
      },
      {
        "question": "会占用很多内存吗？",
        "answer": "日语读音打开时大约占用 170 MB 内存，关掉开关就会释放。其他语言占用很少。"
      },
      {
        "question": "清除已下载的模型会删掉词典吗？",
        "answer": "会。下次打开日语读音时会重新下载。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "読みの表示：相手の言語の読み方を見る",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "読みの表示は、相手側の言語の読み方を、文字の上に小さな文字で表示します。中国語にはピンイン、日本語の漢字にはかな、韓国語にはローマ字、ロシア語とウクライナ語にはラテン文字が付きます。訳文を声に出して読むときや、相手の言葉を目で追うときに、発音がわからなくても役立ちます。読みは画面上にだけ表示され、AI プロバイダーには送信されません。会話の記録、エクスポート、コピーしたテキストもそのままです。"
  },
  "steps": [
    {
      "title": "読みをオンにする",
      "content": "会話の上にあるツールバーの歯車をクリックして、表示設定を開きます。ターゲット言語に読みがある場合、パネルにその言語名の付いたスイッチが表示されます。ここでは<strong>日本語の読みを表示（かな）</strong>です。これをオンにします。",
      "screenshot": "01-switch.png"
    },
    {
      "title": "読みが付く行",
      "content": "読みは、ターゲット言語のすべての行に付きます。あなたの訳文と、相手がその言語で話した発言です。ほかの言語の行はそのままです。ここでは中国語が日本語に翻訳されています。自分の訳文にかなが付き、相手が日本語で話した内容にも付いています。対面での会話では、各行がそれぞれの言語に従います。",
      "screenshot": "02-japanese.png"
    },
    {
      "title": "中国語：ピンイン",
      "content": "各文字に、声調記号付きのピンインが付きます。複数の読みがある文字は文脈で読みが決まります（还 hái、重新 chóng、银行 háng）。一と不の声調変化も、実際の発音どおりに表示されます（一趟 yí tàng）。",
      "screenshot": "03-chinese.png"
    },
    {
      "title": "韓国語：ローマ字",
      "content": "各単語に、改訂ローマ字表記（Revised Romanization）が付きます。1 文字ずつではなく、単語の発音に合わせて表記されます：같이 gachi、국물이 gungmuri、신라호텔 sillahotel。",
      "screenshot": "04-korean.png"
    },
    {
      "title": "ロシア語とウクライナ語：ラテン文字",
      "content": "各単語がラテン文字で表記されます。ロシア語は BGN/PCGN 方式（Здравствуйте zdravstvuyte、всё vsyo）、ウクライナ語は 2010 年の国家規格（Київ kyiv）です。ラテン文字は綴りに対応しており、単語の発音と一致するとは限りません。",
      "screenshot": "05-russian.png"
    },
    {
      "title": "日本語：かなと辞書",
      "content": "かなは漢字の上にだけ付き、ひらがなとカタカナはそのままです。日本語の読みには 17 MB の辞書が必要で、初めてオンにしたときにダウンロードされ、以後は保存されます。ダウンロード中はパネルに進行状況が表示されます。失敗した場合や日本語の読みが使えなくなった場合は、パネルにその旨が表示され、<strong>再試行</strong>を選べます。",
      "screenshot": "06-download.png"
    },
    {
      "title": "字幕ウィンドウで",
      "content": "デスクトップアプリの字幕ウィンドウには独自の表示設定と読みのスイッチがあり、会話の設定とは独立しています。読みは字幕を展開しているときに表示され、コンパクト表示では表示されません。ブラウザ拡張機能では、読みはサイドパネルに表示され、会議ページに重ねて表示される字幕には表示されません。"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "スイッチが表示されるのは、ターゲット言語に読みがある場合だけです。中国語（簡体字）、日本語、韓国語、ロシア語、ウクライナ語が対象です。繁体字中国語と広東語にはまだありません。",
      "中国語 ⇄ 英語のような双方向のターゲットでは、中国語側に読みが付きます。",
      "行がまだ話されている途中のときは、最後の単語の読みは、その行が終わってから付きます。",
      "中国語、韓国語、ロシア語、ウクライナ語の読みにはダウンロードは不要です。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "日本語の読みが間違っているのはなぜですか？",
        "answer": "辞書は単語ごとに読みを 1 つしか持たず、意味からどの読みかを判断できません。たとえば「電車が空いている」の 空いている は すいている（混んでいない）と読むべきところを あいている と表示し、「このカレーは辛い」の 辛い は からい と読むべきところを つらい と表示します。数字と助数詞（一人、3日、4月1日）と お腹が空いた は、正しく読めるように処理しています。令和 のように辞書より新しい単語も、読みを間違えることがあります。"
      },
      {
        "question": "日本語でローマ字を表示できますか？",
        "answer": "まだできません。日本語は今のところかなを表示します。"
      },
      {
        "question": "読みにインターネットは必要ですか？",
        "answer": "読みはお使いのコンピューター上で生成されます。ダウンロードが必要なのは日本語の辞書だけで、1 回だけです。"
      },
      {
        "question": "メモリを多く使いますか？",
        "answer": "日本語の読みはオンの間、約 170 MB を使い、スイッチをオフにすると解放されます。ほかの言語の使用量はごくわずかです。"
      },
      {
        "question": "ダウンロードしたモデルを消去すると、辞書も消えますか？",
        "answer": "はい。次に日本語の読みをオンにしたときに、もう一度ダウンロードされます。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "읽는 법 표시: 상대 언어의 발음 보기",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "읽는 법 표시는 상대방 언어의 발음을 글자 위에 작은 글씨로 보여 줍니다. 중국어에는 병음, 일본어 한자에는 가나, 한국어에는 로마자, 러시아어와 우크라이나어에는 라틴 문자가 붙습니다. 번역문을 소리 내어 읽거나 상대방의 말을 눈으로 따라갈 때, 발음을 몰라도 도움이 됩니다. 읽는 법은 화면에만 표시되며 AI 제공업체로 전송되지 않습니다. 대화 기록, 내보내기, 복사한 텍스트는 그대로 유지됩니다."
  },
  "steps": [
    {
      "title": "읽는 법 켜기",
      "content": "대화 위 도구 모음의 톱니바퀴를 클릭해 표시 설정을 엽니다. 대상 언어에 읽는 법이 있으면 패널에 그 언어 이름이 적힌 스위치가 나타납니다. 여기서는 <strong>일본어 읽는 법 표시(가나)</strong>입니다. 이 스위치를 켜세요.",
      "screenshot": "01-switch.png"
    },
    {
      "title": "읽는 법이 붙는 줄",
      "content": "읽는 법은 대상 언어로 된 모든 줄에 붙습니다. 내 번역문과, 상대방이 그 언어로 말한 내용입니다. 다른 언어의 줄은 그대로입니다. 여기서는 중국어가 일본어로 번역됩니다. 내 번역문에 가나가 붙고, 상대방이 일본어로 말한 내용에도 붙습니다. 대면 대화에서는 각 줄이 자신의 언어를 따릅니다.",
      "screenshot": "02-japanese.png"
    },
    {
      "title": "중국어: 병음",
      "content": "각 글자에 성조 부호가 있는 병음이 붙습니다. 음이 여러 개인 글자는 문맥에 따라 읽습니다(还 hái, 重新 chóng, 银行 háng). 一와 不의 성조 변화도 실제 발음대로 표시됩니다(一趟 yí tàng).",
      "screenshot": "03-chinese.png"
    },
    {
      "title": "한국어: 로마자",
      "content": "각 단어에 국어의 로마자 표기법(Revised Romanization)이 붙습니다. 글자 하나씩이 아니라 단어가 소리 나는 대로 적습니다: 같이 gachi, 국물이 gungmuri, 신라호텔 sillahotel.",
      "screenshot": "04-korean.png"
    },
    {
      "title": "러시아어와 우크라이나어: 라틴 문자",
      "content": "각 단어를 라틴 문자로 적습니다. 러시아어는 BGN/PCGN 방식(Здравствуйте zdravstvuyte, всё vsyo), 우크라이나어는 2010년 국가 표준(Київ kyiv)을 따릅니다. 라틴 문자는 철자를 따르므로 단어의 실제 발음과 항상 같지는 않습니다.",
      "screenshot": "05-russian.png"
    },
    {
      "title": "일본어: 가나와 사전",
      "content": "가나는 한자 위에만 붙고 히라가나와 가타카나는 그대로 둡니다. 일본어 읽는 법에는 17 MB 사전이 필요하며, 처음 켤 때 다운로드되고 이후에도 보관됩니다. 다운로드하는 동안 패널에 진행 상황이 표시됩니다. 다운로드에 실패하거나 일본어 읽는 법이 작동하지 않으면 패널에 그 내용이 표시되고 <strong>재시도</strong>를 선택할 수 있습니다.",
      "screenshot": "06-download.png"
    },
    {
      "title": "자막 창에서",
      "content": "데스크톱 앱의 자막 창에는 자체 표시 설정과 읽는 법 스위치가 있으며, 대화의 설정과는 별개입니다. 읽는 법은 자막을 펼쳤을 때 표시되고 간략 보기에서는 표시되지 않습니다. 브라우저 확장 프로그램은 읽는 법을 사이드 패널에 표시하며, 회의 페이지 위에 그리는 자막에는 표시하지 않습니다."
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "스위치는 대상 언어에 읽는 법이 있을 때만 나타납니다. 중국어(간체), 일본어, 한국어, 러시아어, 우크라이나어입니다. 중국어 번체와 광둥어는 아직 없습니다.",
      "중국어 ⇄ 영어처럼 양방향 대상 언어에서는 중국어 쪽에 읽는 법이 붙습니다.",
      "줄을 아직 말하는 중일 때는 마지막 단어의 읽는 법이 그 줄이 끝난 뒤에 붙습니다.",
      "중국어, 한국어, 러시아어, 우크라이나어의 읽는 법은 다운로드가 필요 없습니다."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "일본어 읽는 법이 틀리는 이유는 무엇인가요?",
        "answer": "사전은 단어마다 읽는 법을 하나만 갖고 있어서 뜻을 보고 어느 쪽인지 판단하지 못합니다. 空いている는 電車が空いている처럼 すいている(붐비지 않음)로 읽어야 할 때도 あいている(비어 있음)로 읽고, 辛い는 매운 카레에서처럼 からい로 읽어야 할 때도 つらい(괴로움)로 읽습니다. 숫자와 단위(一人, 3日, 4月1日)와 お腹が空いた는 처리됩니다. 令和처럼 사전보다 새로운 단어는 잘못 읽을 수도 있습니다."
      },
      {
        "question": "일본어를 로마자로 표시할 수 있나요?",
        "answer": "아직은 안 됩니다. 일본어는 현재 가나로 표시됩니다."
      },
      {
        "question": "읽는 법에 인터넷이 필요한가요?",
        "answer": "읽는 법은 사용자의 컴퓨터에서 만들어집니다. 다운로드하는 것은 일본어 사전뿐이며, 한 번만 받습니다."
      },
      {
        "question": "메모리를 많이 쓰나요?",
        "answer": "일본어 읽는 법은 켜 둔 동안 약 170 MB를 사용하며, 스위치를 끄면 해제됩니다. 다른 언어는 거의 쓰지 않습니다."
      },
      {
        "question": "다운로드한 모델을 지우면 사전도 사라지나요?",
        "answer": "네. 다음에 일본어 읽는 법을 켤 때 다시 다운로드됩니다."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "Lectura de la pronunciación: ver cómo se dice el otro idioma",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "La lectura de la pronunciación muestra cómo se pronuncia el idioma de la otra parte, en texto pequeño sobre las palabras: pinyin sobre el chino, kana sobre los kanji del japonés, romanización sobre el coreano y letras latinas sobre el ruso y el ucraniano. Sirve cuando lees una traducción en voz alta, o sigues lo que dice la otra parte, sin saber cómo suenan las palabras. La lectura solo se dibuja en pantalla: nunca se envía al proveedor de IA, y la transcripción, las exportaciones y el texto copiado quedan como estaban."
  },
  "steps": [
    {
      "title": "Activar la lectura",
      "content": "Haz clic en el engranaje de la barra de herramientas situada sobre la conversación para abrir los ajustes de visualización. Cuando tu idioma de destino tiene lectura, el panel incluye un interruptor que lo nombra, aquí <strong>Mostrar la lectura en japonés (kana)</strong>. Actívalo.",
      "screenshot": "01-switch.png"
    },
    {
      "title": "Qué líneas tienen lectura",
      "content": "La lectura se añade a todas las líneas en tu idioma de destino: tus traducciones y las palabras de la otra parte cuando hablan ese idioma. Las líneas en otros idiomas quedan como están. Aquí el chino se traduce al japonés: mis traducciones llevan kana, y también lo que dice la otra parte en japonés. En una conversación cara a cara, cada línea sigue su propio idioma.",
      "screenshot": "02-japanese.png"
    },
    {
      "title": "Chino: pinyin",
      "content": "Cada carácter lleva su pinyin, con marcas de tono. Un carácter con más de una lectura se lee según el contexto (还 hái, 重新 chóng, 银行 háng), y los cambios de tono de 一 y 不 se muestran tal como se pronuncian (一趟 yí tàng).",
      "screenshot": "03-chinese.png"
    },
    {
      "title": "Coreano: romanización",
      "content": "Cada palabra lleva su romanización revisada, escrita como suena la palabra y no letra por letra: 같이 gachi, 국물이 gungmuri, 신라호텔 sillahotel.",
      "screenshot": "04-korean.png"
    },
    {
      "title": "Ruso y ucraniano: letras latinas",
      "content": "Cada palabra se escribe con letras latinas: el ruso con el sistema BGN/PCGN (Здравствуйте zdravstvuyte, всё vsyo) y el ucraniano con el sistema nacional de 2010 (Київ kyiv). Las letras siguen la ortografía, que no siempre es como suena la palabra.",
      "screenshot": "05-russian.png"
    },
    {
      "title": "Japonés: kana y el diccionario",
      "content": "El kana se coloca solo sobre los kanji; el hiragana y el katakana quedan como están. La lectura del japonés necesita un diccionario de 17 MB, que se descarga la primera vez que la activas y se conserva para después. El panel muestra la descarga mientras se realiza. Si falla, o si la lectura del japonés deja de funcionar, el panel lo indica y ofrece <strong>Reintentar</strong>.",
      "screenshot": "06-download.png"
    },
    {
      "title": "En la ventana de subtítulos",
      "content": "En la aplicación de escritorio, la ventana de subtítulos tiene sus propios ajustes de visualización, con su propio interruptor de lectura, independiente del de la conversación. La lectura se muestra mientras los subtítulos están expandidos, no en la vista compacta. La extensión del navegador muestra la lectura en su panel lateral, no en los subtítulos que dibuja sobre la página de la reunión."
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "El interruptor solo aparece cuando tu idioma de destino tiene lectura: chino (simplificado), japonés, coreano, ruso o ucraniano. El chino tradicional y el cantonés todavía no la tienen.",
      "Con un destino bidireccional como chino ⇄ inglés, la mitad en chino lleva lectura.",
      "Mientras una línea todavía se está diciendo, su última palabra recibe la lectura cuando la línea termina.",
      "La lectura del chino, el coreano, el ruso y el ucraniano no necesita descargas."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "¿Por qué una lectura del japonés es incorrecta?",
        "answer": "El diccionario da una sola lectura por palabra y no puede saber por el significado cuál se quiere. 空いている se lee あいている (vacío) incluso en 電車が空いている, donde se quiere すいている (poco concurrido), y 辛い se lee つらい (doloroso) incluso para un curry picante, donde se quiere からい. Los números con contadores (一人, 3日, 4月1日) y お腹が空いた se manejan bien. Una palabra más nueva que el diccionario, como 令和, también puede leerse mal."
      },
      {
        "question": "¿Puedo obtener rōmaji para el japonés?",
        "answer": "Todavía no. Por ahora el japonés muestra kana."
      },
      {
        "question": "¿La lectura necesita internet?",
        "answer": "La lectura se calcula en tu ordenador. La única descarga es el diccionario de japonés, una sola vez."
      },
      {
        "question": "¿Usa mucha memoria?",
        "answer": "La lectura del japonés usa unos 170 MB mientras está activada, y al desactivar el interruptor se libera. Los demás idiomas usan muy poca."
      },
      {
        "question": "¿Borrar los modelos descargados elimina el diccionario?",
        "answer": "Sí. Se vuelve a descargar la próxima vez que actives la lectura del japonés."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "Lectures de prononciation : voir comment se dit l’autre langue",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "Les lectures de prononciation montrent comment se prononce la langue de l’autre côté, en petit texte au-dessus des mots : pinyin sur le chinois, kana sur les kanji du japonais, romanisation sur le coréen et lettres latines sur le russe et l’ukrainien. Elles aident quand vous lisez une traduction à voix haute, ou suivez votre interlocuteur, sans savoir comment sonnent les mots. Les lectures ne sont dessinées qu’à l’écran : elles ne sont jamais envoyées au fournisseur d’IA, et la transcription, les exports et le texte copié restent tels qu’ils étaient."
  },
  "steps": [
    {
      "title": "Activer les lectures",
      "content": "Cliquez sur l’engrenage de la barre d’outils au-dessus de la conversation pour ouvrir les paramètres d’affichage. Quand votre langue cible a des lectures, le panneau contient un interrupteur qui la nomme, ici <strong>Afficher les lectures en japonais (kana)</strong>. Activez-le.",
      "screenshot": "01-switch.png"
    },
    {
      "title": "Quelles lignes ont des lectures",
      "content": "Les lectures s’ajoutent à toutes les lignes dans votre langue cible : vos traductions, et les propres mots de votre interlocuteur quand il parle cette langue. Les lignes dans d’autres langues restent telles quelles. Ici, le chinois est traduit en japonais : mes traductions reçoivent des kana, tout comme ce que l’interlocuteur dit en japonais. Dans une conversation en face à face, chaque ligne suit sa propre langue.",
      "screenshot": "02-japanese.png"
    },
    {
      "title": "Chinois : pinyin",
      "content": "Chaque caractère reçoit son pinyin, avec les marques de ton. Un caractère qui a plusieurs lectures est lu selon le contexte (还 hái, 重新 chóng, 银行 háng), et les changements de ton de 一 et de 不 sont indiqués tels qu’ils se prononcent (一趟 yí tàng).",
      "screenshot": "03-chinese.png"
    },
    {
      "title": "Coréen : romanisation",
      "content": "Chaque mot reçoit sa romanisation révisée, écrite comme le mot sonne et non lettre par lettre : 같이 gachi, 국물이 gungmuri, 신라호텔 sillahotel.",
      "screenshot": "04-korean.png"
    },
    {
      "title": "Russe et ukrainien : lettres latines",
      "content": "Chaque mot est épelé en lettres latines : le russe selon le système BGN/PCGN (Здравствуйте zdravstvuyte, всё vsyo), l’ukrainien selon le système national de 2010 (Київ kyiv). Les lettres suivent l’orthographe, qui n’est pas toujours la façon dont un mot sonne.",
      "screenshot": "05-russian.png"
    },
    {
      "title": "Japonais : kana et dictionnaire",
      "content": "Les kana se placent uniquement sur les kanji ; les hiragana et les katakana restent tels quels. Les lectures du japonais demandent un dictionnaire de 17 MB, qui se télécharge la première fois que vous les activez et reste conservé pour la suite. Le panneau affiche le téléchargement pendant qu’il se déroule. S’il échoue, ou si les lectures du japonais cessent de fonctionner, le panneau l’indique et propose <strong>Réessayer</strong>.",
      "screenshot": "06-download.png"
    },
    {
      "title": "Dans la fenêtre de sous-titres",
      "content": "Dans l’application de bureau, la fenêtre de sous-titres a ses propres paramètres d’affichage, avec son propre interrupteur de lectures, indépendant de celui de la conversation. Les lectures s’affichent quand les sous-titres sont développés, pas dans la vue compacte. L’extension de navigateur affiche les lectures dans son panneau latéral, pas dans les sous-titres qu’elle dessine sur la page de la réunion."
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "L’interrupteur n’apparaît que si votre langue cible a des lectures : chinois (simplifié), japonais, coréen, russe ou ukrainien. Le chinois traditionnel et le cantonais n’en ont pas encore.",
      "Avec une cible bidirectionnelle comme chinois ⇄ anglais, la moitié chinoise reçoit des lectures.",
      "Tant qu’une ligne est encore en train d’être dite, son dernier mot reçoit sa lecture une fois la ligne terminée.",
      "Les lectures du chinois, du coréen, du russe et de l’ukrainien n’exigent aucun téléchargement."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "Pourquoi une lecture du japonais est-elle fausse ?",
        "answer": "Le dictionnaire donne une seule lecture par mot et ne peut pas deviner d’après le sens laquelle est voulue. 空いている se lit あいている (libre) même dans 電車が空いている, où すいている (peu fréquenté) est voulu, et 辛い se lit つらい (pénible) même pour un curry épicé, où からい est voulu. Les nombres avec compteurs (一人, 3日, 4月1日) et お腹が空いた sont pris en charge. Un mot plus récent que le dictionnaire, comme 令和, peut aussi être mal lu."
      },
      {
        "question": "Puis-je obtenir le rōmaji pour le japonais ?",
        "answer": "Pas encore. Pour l’instant, le japonais affiche des kana."
      },
      {
        "question": "Les lectures nécessitent-elles Internet ?",
        "answer": "Les lectures sont calculées sur votre ordinateur. Le seul téléchargement est le dictionnaire japonais, une seule fois."
      },
      {
        "question": "Utilisent-elles beaucoup de mémoire ?",
        "answer": "Les lectures du japonais utilisent environ 170 MB tant qu’elles sont activées, et désactiver l’interrupteur libère cette mémoire. Les autres langues en utilisent très peu."
      },
      {
        "question": "Effacer mes modèles téléchargés supprime-t-il le dictionnaire ?",
        "answer": "Oui. Il se télécharge à nouveau la prochaine fois que vous activez les lectures du japonais."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "قراءات النطق: اطّلع على كيفية نطق اللغة الأخرى",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "تعرض القراءات طريقة نطق لغة الطرف الآخر بنص صغير فوق الكلمات: البينيين فوق الصينية، والكانا فوق الكانجي الياباني، والكتابة اللاتينية فوق الكورية، والأحرف اللاتينية فوق الروسية والأوكرانية. وهي تفيدك حين تقرأ ترجمة بصوت عالٍ، أو حين تتابع كلام الطرف الآخر دون أن تعرف كيف تُنطق كلماته. تُرسم القراءات على الشاشة فقط: فلا تُرسل إلى مزوّد الذكاء الاصطناعي، ويبقى سجل المحادثة والملفات المصدَّرة والنص المنسوخ كما هي."
  },
  "steps": [
    {
      "title": "تشغيل القراءات",
      "content": "انقر على الترس في شريط الأدوات فوق المحادثة لفتح إعدادات العرض. إذا كانت للغة الهدف قراءات، فستجد في اللوحة مفتاحًا يسمّيها، وهو هنا <strong>إظهار قراءات اليابانية (الكانا)</strong>. شغّله.",
      "screenshot": "01-switch.png"
    },
    {
      "title": "الأسطر التي تحصل على قراءات",
      "content": "تُضاف القراءات إلى كل سطر بلغتك الهدف: ترجماتك، وكلام الطرف الآخر نفسه حين يتحدث بها. أما الأسطر بلغات أخرى فتبقى كما هي. هنا تُترجم الصينية إلى اليابانية: ترجماتي تحصل على الكانا، وكذلك ما يقوله الطرف الآخر بالياباني. وفي المحادثة وجهًا لوجه، يتبع كل سطر لغته.",
      "screenshot": "02-japanese.png"
    },
    {
      "title": "الصينية: البينيين",
      "content": "يحصل كل حرف على البينيين الخاص به مع علامات النغمة. والحرف الذي له أكثر من قراءة يُقرأ بحسب السياق (⁦还 hái⁩، ⁦重新 chóng⁩، ⁦银行 háng⁩)، وتُعرض تغيّرات نغمة ⁦一⁩ و⁦不⁩ كما تُنطق (⁦一趟 yí tàng⁩).",
      "screenshot": "03-chinese.png"
    },
    {
      "title": "الكورية: الكتابة اللاتينية",
      "content": "تحصل كل كلمة على كتابتها اللاتينية وفق نظام الكتابة اللاتينية المنقّح، مكتوبة كما تُنطق الكلمة لا حرفًا بحرف: ⁦같이 gachi⁩، ⁦국물이 gungmuri⁩، ⁦신라호텔 sillahotel⁩.",
      "screenshot": "04-korean.png"
    },
    {
      "title": "الروسية والأوكرانية: الأحرف اللاتينية",
      "content": "تُكتب كل كلمة بأحرف لاتينية: الروسية وفق نظام ⁦BGN/PCGN⁩ (⁦Здравствуйте zdravstvuyte⁩، ⁦всё vsyo⁩)، والأوكرانية وفق النظام الوطني لعام 2010 (⁦Київ kyiv⁩). وتتبع الأحرف الإملاء، وهو ليس دائمًا طريقة نطق الكلمة.",
      "screenshot": "05-russian.png"
    },
    {
      "title": "اليابانية: الكانا والقاموس",
      "content": "تُوضع الكانا فوق الكانجي فقط، أما الهيراغانا والكاتاكانا فتبقيان كما هما. تحتاج القراءات اليابانية إلى قاموس حجمه ⁦17 MB⁩، يُنزَّل عند تشغيلها أول مرة ويُحفظ لما بعد ذلك. تعرض اللوحة التنزيل أثناء سيره. وإذا فشل، أو توقفت القراءات اليابانية عن العمل، فتُخبرك اللوحة بذلك وتعرض <strong>إعادة المحاولة</strong>.",
      "screenshot": "06-download.png"
    },
    {
      "title": "في عرض الترجمة النصية",
      "content": "في تطبيق سطح المكتب، لعرض الترجمة النصية إعدادات عرض خاصة به، فيها مفتاح قراءات مستقل عن مفتاح المحادثة. تظهر القراءات حين تكون الترجمة النصية موسّعة، لا في العرض المضغوط. أما امتداد المتصفح فيعرض القراءات في لوحته الجانبية، لا في الترجمة النصية التي يرسمها فوق صفحة الاجتماع."
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "لا يظهر المفتاح إلا إذا كانت للغة الهدف قراءات: الصينية (المبسطة) أو اليابانية أو الكورية أو الروسية أو الأوكرانية. أما الصينية التقليدية والكانتونية فليست لهما قراءات حتى الآن.",
      "مع هدف ثنائي الاتجاه مثل ⁦الصينية ⇄ الإنجليزية⁩، يحصل النصف الصيني على القراءات.",
      "ما دام السطر يُنطق بعد، تظهر قراءة آخر كلمة فيه عند انتهاء السطر.",
      "لا تحتاج القراءات الصينية والكورية والروسية والأوكرانية إلى أي تنزيل."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "لماذا تكون القراءة اليابانية خاطئة أحيانًا؟",
        "answer": "يعطي القاموس كل كلمة قراءة واحدة، ولا يستطيع أن يعرف من المعنى أيّها المقصودة. فـ⁦空いている⁩ تُقرأ ⁦あいている⁩ (شاغر) حتى في ⁦電車が空いている⁩، حيث المقصود ⁦すいている⁩ (غير مزدحم)، وتُقرأ ⁦辛い⁩ ⁦つらい⁩ (مؤلم) حتى مع كاري حار، حيث المقصود ⁦からい⁩. أما الأعداد مع المعدِّدات (⁦一人⁩، ⁦3日⁩، ⁦4月1日⁩) و⁦お腹が空いた⁩ فهي معالَجة. وقد تُقرأ كلمة أحدث من القاموس، مثل ⁦令和⁩، قراءة خاطئة أيضًا."
      },
      {
        "question": "هل يمكنني الحصول على الروماجي للغة اليابانية؟",
        "answer": "ليس بعد. تعرض اليابانية الكانا حاليًا."
      },
      {
        "question": "هل تحتاج القراءات إلى الإنترنت؟",
        "answer": "تُحسب القراءات على حاسوبك. والتنزيل الوحيد هو القاموس الياباني، مرة واحدة."
      },
      {
        "question": "هل تستهلك ذاكرة كثيرة؟",
        "answer": "تستهلك القراءات اليابانية نحو ⁦170 MB⁩ ما دامت مفعّلة، وإيقاف المفتاح يحرّرها. أما اللغات الأخرى فتستهلك القليل جدًا."
      },
      {
        "question": "هل يؤدي مسح النماذج التي نزّلتها إلى إزالة القاموس؟",
        "answer": "نعم. يُنزَّل من جديد عند تشغيل القراءات اليابانية في المرة التالية."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "Leituras de pronúncia: veja como a outra língua é falada",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "As leituras mostram como a língua da outra pessoa é pronunciada, em texto pequeno acima das palavras: pinyin sobre o chinês, kana sobre os kanji japoneses, romanização sobre o coreano e letras latinas sobre o russo e o ucraniano. Elas ajudam quando você lê uma tradução em voz alta, ou acompanha a outra pessoa, sem saber como as palavras soam. As leituras só são desenhadas na tela: nunca são enviadas ao provedor de IA, e a transcrição, as exportações e o texto copiado continuam como estavam."
  },
  "steps": [
    {
      "title": "Ative as leituras",
      "content": "Clique na engrenagem da barra de ferramentas acima da conversa para abrir as configurações de exibição. Quando o seu idioma de destino tem leituras, o painel tem uma chave que o nomeia, aqui <strong>Mostrar leituras em japonês (kana)</strong>. Ative-a.",
      "screenshot": "01-switch.png"
    },
    {
      "title": "Quais falas recebem leituras",
      "content": "As leituras vão em todas as falas no seu idioma de destino: as suas traduções e as palavras da outra pessoa quando ela fala nesse idioma. As falas em outros idiomas ficam como estão. Aqui o chinês é traduzido para o japonês: as minhas traduções recebem kana, e o que a outra pessoa diz em japonês também. Em uma conversa cara a cara, cada fala segue o seu próprio idioma.",
      "screenshot": "02-japanese.png"
    },
    {
      "title": "Chinês: pinyin",
      "content": "Cada caractere recebe o seu pinyin, com marcas de tom. Um caractere com mais de uma leitura é lido conforme o contexto (还 hái, 重新 chóng, 银行 háng), e as mudanças de tom de 一 e 不 aparecem como são faladas (一趟 yí tàng).",
      "screenshot": "03-chinese.png"
    },
    {
      "title": "Coreano: romanização",
      "content": "Cada palavra recebe a sua Romanização Revisada, escrita como a palavra soa, e não letra por letra: 같이 gachi, 국물이 gungmuri, 신라호텔 sillahotel.",
      "screenshot": "04-korean.png"
    },
    {
      "title": "Russo e ucraniano: letras latinas",
      "content": "Cada palavra é escrita em letras latinas: o russo no sistema BGN/PCGN (Здравствуйте zdravstvuyte, всё vsyo) e o ucraniano no sistema nacional de 2010 (Київ kyiv). As letras seguem a grafia, que nem sempre é como a palavra soa.",
      "screenshot": "05-russian.png"
    },
    {
      "title": "Japonês: kana e o dicionário",
      "content": "O kana vai só sobre os kanji; hiragana e katakana ficam como estão. As leituras em japonês precisam de um dicionário de 17 MB, que é baixado na primeira vez que você as ativa e fica guardado para depois. O painel mostra o download enquanto ele acontece. Se ele falhar, ou se as leituras em japonês pararem de funcionar, o painel avisa e oferece <strong>Tentar novamente</strong>.",
      "screenshot": "06-download.png"
    },
    {
      "title": "Na visualização de legendas",
      "content": "No app para desktop, a visualização de legendas tem as suas próprias configurações de exibição, com a sua própria chave de leituras, independente da da conversa. As leituras aparecem enquanto as legendas estão expandidas, e não na visualização compacta. A extensão do navegador mostra as leituras no painel lateral, e não nas legendas que desenha sobre a página da reunião."
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "A chave só aparece quando o seu idioma de destino tem leituras: chinês (simplificado), japonês, coreano, russo ou ucraniano. O chinês tradicional e o cantonês ainda não têm.",
      "Com um destino de dois sentidos, como chinês ⇄ inglês, a metade em chinês recebe leituras.",
      "Enquanto uma fala ainda está sendo dita, a leitura da última palavra aparece quando a fala termina.",
      "As leituras em chinês, coreano, russo e ucraniano não precisam de download."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "Por que uma leitura em japonês está errada?",
        "answer": "O dicionário dá uma única leitura para cada palavra e não consegue saber pelo sentido qual delas é a pretendida. 空いている é lido あいている (vago) mesmo em 電車が空いている, onde se quer dizer すいている (não lotado), e 辛い é lido つらい (doloroso) mesmo para um curry picante, onde se quer dizer からい. Números com contadores (一人, 3日, 4月1日) e お腹が空いた são tratados. Uma palavra mais nova que o dicionário, como 令和, também pode ser lida errado."
      },
      {
        "question": "Posso ter romaji para o japonês?",
        "answer": "Ainda não. Por enquanto, o japonês mostra kana."
      },
      {
        "question": "As leituras precisam de internet?",
        "answer": "As leituras são calculadas no seu computador. O único download é o dicionário de japonês, uma vez."
      },
      {
        "question": "Elas usam muita memória?",
        "answer": "As leituras em japonês usam cerca de 170 MB enquanto estão ativadas, e desligar a chave libera essa memória. Os outros idiomas usam muito pouco."
      },
      {
        "question": "Limpar os modelos baixados remove o dicionário?",
        "answer": "Sim. Ele é baixado de novo na próxima vez que as leituras em japonês forem ativadas."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Чтения: как произносится другой язык",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "Чтения показывают, как произносится язык собеседника, мелким текстом над словами: пиньинь над китайским, кана над японскими иероглифами, романизация над корейским и латиница над русским и украинским. Они помогают, когда вы читаете перевод вслух или следите за собеседником, не зная, как звучат слова. Чтения только рисуются на экране: они не отправляются AI-провайдеру, а запись разговора, экспорт и скопированный текст остаются прежними."
  },
  "steps": [
    {
      "title": "Включите чтения",
      "content": "Нажмите шестерёнку на панели инструментов над разговором, чтобы открыть настройки отображения. Если у целевого языка есть чтения, на панели появится переключатель с названием этого языка, здесь <strong>Показывать чтения (японский, кана)</strong>. Включите его.",
      "screenshot": "01-switch.png"
    },
    {
      "title": "На каких строках есть чтения",
      "content": "Чтения добавляются к каждой строке на вашем целевом языке: к вашим переводам и к словам собеседника, когда он говорит на этом языке. Строки на других языках остаются как есть. Здесь китайский переводится на японский: к моим переводам добавлена кана, как и к тому, что собеседник говорит по-японски. При разговоре лицом к лицу каждая строка следует своему языку.",
      "screenshot": "02-japanese.png"
    },
    {
      "title": "Китайский: пиньинь",
      "content": "К каждому иероглифу добавляется пиньинь со знаками тонов. Иероглиф с несколькими чтениями читается по контексту (还 hái, 重新 chóng, 银行 háng), а изменения тонов у 一 и 不 показаны так, как они произносятся (一趟 yí tàng).",
      "screenshot": "03-chinese.png"
    },
    {
      "title": "Корейский: романизация",
      "content": "К каждому слову добавляется его запись по системе Revised Romanization, так, как слово звучит, а не буква за буквой: 같이 gachi, 국물이 gungmuri, 신라호텔 sillahotel.",
      "screenshot": "04-korean.png"
    },
    {
      "title": "Русский и украинский: латиница",
      "content": "Каждое слово записывается латинскими буквами: русское по системе BGN/PCGN (Здравствуйте zdravstvuyte, всё vsyo), украинское по национальной системе 2010 года (Київ kyiv). Буквы следуют написанию, а оно не всегда совпадает с тем, как слово звучит.",
      "screenshot": "05-russian.png"
    },
    {
      "title": "Японский: кана и словарь",
      "content": "Кана ставится только над иероглифами; хирагана и катакана остаются как есть. Для японских чтений нужен словарь размером 17 MB: он загружается при первом включении и сохраняется на будущее. Пока идёт загрузка, панель показывает её ход. Если загрузка не удалась или японские чтения перестали работать, панель сообщает об этом и предлагает <strong>Повторить</strong>.",
      "screenshot": "06-download.png"
    },
    {
      "title": "В окне субтитров",
      "content": "В настольном приложении у окна субтитров есть свои настройки отображения со своим переключателем чтений, независимым от переключателя разговора. Чтения показываются, пока субтитры развёрнуты, но не в компактном виде. Расширение для браузера показывает чтения на боковой панели, но не в субтитрах, которые оно рисует поверх страницы встречи."
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "Переключатель появляется, только если у целевого языка есть чтения: китайский (упрощённый), японский, корейский, русский или украинский. У традиционного китайского и кантонского их пока нет.",
      "Если целевое направление двустороннее, например китайский ⇄ английский, чтения получает китайская половина.",
      "Пока строка ещё произносится, чтение её последнего слова появляется, когда строка закончится.",
      "Для китайских, корейских, русских и украинских чтений загрузка не нужна."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Почему японское чтение бывает неверным?",
        "answer": "Словарь даёт каждому слову одно чтение и не может по смыслу понять, какое имеется в виду. 空いている читается あいている (свободно) даже в 電車が空いている, где имеется в виду すいている (не людно), а 辛い читается つらい (тяжело) даже для острого карри, где имеется в виду からい. Числа со счётными словами (一人, 3日, 4月1日) и お腹が空いた обрабатываются. Слово новее словаря, например 令和, тоже может быть прочитано неверно."
      },
      {
        "question": "Можно ли получить ромадзи для японского?",
        "answer": "Пока нет. Сейчас для японского показывается кана."
      },
      {
        "question": "Нужен ли для чтений интернет?",
        "answer": "Чтения вычисляются на вашем компьютере. Скачать нужно только японский словарь, и только один раз."
      },
      {
        "question": "Много ли они занимают памяти?",
        "answer": "Японские чтения занимают около 170 MB, пока включены, а при выключении переключателя память освобождается. Остальные языки занимают очень мало."
      },
      {
        "question": "Удаляется ли словарь, если очистить загруженные модели?",
        "answer": "Да. Он загрузится снова при следующем включении японских чтений."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Aussprache-Lesungen: sehen, wie die andere Sprache gesprochen wird",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "Lesungen zeigen als kleinen Text über den Wörtern, wie die Sprache des Gegenübers ausgesprochen wird: Pinyin über Chinesisch, Kana über japanischen Kanji, Romanisierung über Koreanisch und lateinische Buchstaben über Russisch und Ukrainisch. Sie helfen, wenn Sie eine Übersetzung laut vorlesen oder dem Gegenüber folgen, ohne zu wissen, wie die Wörter klingen. Lesungen werden nur auf dem Bildschirm dargestellt: Sie werden nie an den KI-Anbieter gesendet, und Protokoll, Exporte und kopierter Text bleiben unverändert."
  },
  "steps": [
    {
      "title": "Lesungen einschalten",
      "content": "Klicken Sie auf das Zahnrad in der Symbolleiste über der Unterhaltung, um die Anzeigeeinstellungen zu öffnen. Wenn Ihre Zielsprache Lesungen hat, enthält das Fenster einen Schalter, der sie benennt, hier <strong>Japanisch-Lesungen anzeigen (Kana)</strong>. Schalten Sie ihn ein.",
      "screenshot": "01-switch.png"
    },
    {
      "title": "Welche Zeilen Lesungen erhalten",
      "content": "Lesungen erhalten alle Zeilen in Ihrer Zielsprache: Ihre Übersetzungen und die eigenen Worte des Gegenübers, wenn es diese Sprache spricht. Zeilen in anderen Sprachen bleiben unverändert. Hier wird Chinesisch ins Japanische übersetzt: Meine Übersetzungen erhalten Kana, ebenso das, was das Gegenüber auf Japanisch sagt. In einem Gespräch von Angesicht zu Angesicht richtet sich jede Zeile nach ihrer eigenen Sprache.",
      "screenshot": "02-japanese.png"
    },
    {
      "title": "Chinesisch: Pinyin",
      "content": "Jedes Zeichen erhält sein Pinyin mit Tonzeichen. Ein Zeichen mit mehr als einer Lesung wird im Kontext gelesen (还 hái, 重新 chóng, 银行 háng), und die Tonwechsel von 一 und 不 werden so angezeigt, wie sie gesprochen werden (一趟 yí tàng).",
      "screenshot": "03-chinese.png"
    },
    {
      "title": "Koreanisch: Romanisierung",
      "content": "Jedes Wort erhält die Revidierte Romanisierung, geschrieben so, wie das Wort klingt, und nicht Buchstabe für Buchstabe: 같이 gachi, 국물이 gungmuri, 신라호텔 sillahotel.",
      "screenshot": "04-korean.png"
    },
    {
      "title": "Russisch und Ukrainisch: lateinische Buchstaben",
      "content": "Jedes Wort wird in lateinischen Buchstaben geschrieben: Russisch nach dem System BGN/PCGN (Здравствуйте zdravstvuyte, всё vsyo), Ukrainisch nach dem nationalen System von 2010 (Київ kyiv). Die Buchstaben folgen der Schreibweise, die nicht immer dem Klang eines Wortes entspricht.",
      "screenshot": "05-russian.png"
    },
    {
      "title": "Japanisch: Kana und das Wörterbuch",
      "content": "Kana stehen nur über den Kanji; Hiragana und Katakana bleiben unverändert. Japanische Lesungen brauchen ein Wörterbuch von 17 MB, das beim ersten Einschalten heruntergeladen und für später behalten wird. Das Fenster zeigt den Download an, solange er läuft. Schlägt er fehl oder funktionieren die japanischen Lesungen nicht mehr, meldet das Fenster dies und bietet <strong>Wiederholen</strong> an.",
      "screenshot": "06-download.png"
    },
    {
      "title": "In der Untertitelansicht",
      "content": "In der Desktop-App hat die Untertitelansicht eigene Anzeigeeinstellungen mit einem eigenen Lesungen-Schalter, unabhängig von dem der Unterhaltung. Lesungen werden angezeigt, solange die Untertitel erweitert sind, nicht in der kompakten Ansicht. Die Browser-Erweiterung zeigt Lesungen in ihrer Seitenleiste, nicht in den Untertiteln, die sie über die Meeting-Seite legt."
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "Der Schalter erscheint nur, wenn Ihre Zielsprache Lesungen hat: Chinesisch (vereinfacht), Japanisch, Koreanisch, Russisch oder Ukrainisch. Traditionelles Chinesisch und Kantonesisch haben noch keine.",
      "Bei einem Ziel mit zwei Richtungen wie Chinesisch ⇄ Englisch erhält die chinesische Hälfte Lesungen.",
      "Solange eine Zeile noch gesprochen wird, erhält ihr letztes Wort seine Lesung erst, wenn die Zeile abgeschlossen ist.",
      "Lesungen für Chinesisch, Koreanisch, Russisch und Ukrainisch brauchen keinen Download."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Warum ist eine japanische Lesung falsch?",
        "answer": "Das Wörterbuch gibt jedem Wort eine Lesung und kann an der Bedeutung nicht erkennen, welche gemeint ist. 空いている wird あいている (frei) gelesen, auch in 電車が空いている, wo すいている (nicht überfüllt) gemeint ist, und 辛い wird つらい (schmerzhaft) gelesen, auch bei einem scharfen Curry, wo からい gemeint ist. Zahlen mit Zählwörtern (一人, 3日, 4月1日) und お腹が空いた werden richtig behandelt. Ein Wort, das neuer ist als das Wörterbuch, wie 令和, kann ebenfalls falsch gelesen werden."
      },
      {
        "question": "Gibt es Romaji für Japanisch?",
        "answer": "Noch nicht. Japanisch zeigt vorerst Kana."
      },
      {
        "question": "Brauchen Lesungen das Internet?",
        "answer": "Lesungen werden auf Ihrem Computer berechnet. Der einzige Download ist das japanische Wörterbuch, einmalig."
      },
      {
        "question": "Brauchen sie viel Arbeitsspeicher?",
        "answer": "Japanische Lesungen brauchen etwa 170 MB, solange sie eingeschaltet sind; das Ausschalten des Schalters gibt den Speicher frei. Die anderen Sprachen brauchen sehr wenig."
      },
      {
        "question": "Entfernt das Löschen meiner heruntergeladenen Modelle das Wörterbuch?",
        "answer": "Ja. Es wird beim nächsten Einschalten der japanischen Lesungen erneut heruntergeladen."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Читання: як вимовляється інша мова",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "Читання показують, як вимовляється мова співрозмовника, дрібним текстом над словами: піньїн над китайською, кана над японськими ієрогліфами, романізація над корейською та латиниця над російською й українською. Вони допомагають, коли ви читаєте переклад уголос або стежите за співрозмовником, не знаючи, як звучать слова. Читання лише малюються на екрані: вони не надсилаються AI-провайдеру, а запис розмови, експорт і скопійований текст залишаються такими, як були."
  },
  "steps": [
    {
      "title": "Увімкніть читання",
      "content": "Натисніть шестерню на панелі інструментів над розмовою, щоб відкрити налаштування відображення. Якщо в цільової мови є читання, на панелі з’явиться перемикач із назвою цієї мови, тут <strong>Показувати читання (японська, кана)</strong>. Увімкніть його.",
      "screenshot": "01-switch.png"
    },
    {
      "title": "На яких рядках є читання",
      "content": "Читання додаються до кожного рядка вашою цільовою мовою: до ваших перекладів і до слів співрозмовника, коли він говорить цією мовою. Рядки іншими мовами залишаються як є. Тут китайська перекладається японською: до моїх перекладів додано кану, так само як і до того, що співрозмовник каже японською. Під час розмови віч-на-віч кожен рядок визначається власною мовою.",
      "screenshot": "02-japanese.png"
    },
    {
      "title": "Китайська: піньїн",
      "content": "До кожного ієрогліфа додається піньїн зі знаками тонів. Ієрогліф із кількома читаннями читається за контекстом (还 hái, 重新 chóng, 银行 háng), а зміни тонів у 一 і 不 показано так, як вони вимовляються (一趟 yí tàng).",
      "screenshot": "03-chinese.png"
    },
    {
      "title": "Корейська: романізація",
      "content": "До кожного слова додається його запис за системою Revised Romanization, так, як слово звучить, а не літера за літерою: 같이 gachi, 국물이 gungmuri, 신라호텔 sillahotel.",
      "screenshot": "04-korean.png"
    },
    {
      "title": "Російська й українська: латиниця",
      "content": "Кожне слово записується латинськими літерами: російське за системою BGN/PCGN (Здравствуйте zdravstvuyte, всё vsyo), українське за національною системою 2010 року (Київ kyiv). Літери відповідають написанню, а воно не завжди збігається з тим, як слово звучить.",
      "screenshot": "05-russian.png"
    },
    {
      "title": "Японська: кана і словник",
      "content": "Кана ставиться лише над ієрогліфами; хірагана і катакана залишаються як є. Для японських читань потрібен словник розміром 17 MB: він завантажується під час першого ввімкнення і зберігається на майбутнє. Поки триває завантаження, панель показує його перебіг. Якщо завантаження не вдалося або японські читання перестали працювати, панель повідомляє про це й пропонує <strong>Повторити</strong>.",
      "screenshot": "06-download.png"
    },
    {
      "title": "У вікні субтитрів",
      "content": "У настільному застосунку вікно субтитрів має власні налаштування відображення з власним перемикачем читань, незалежним від перемикача розмови. Читання показуються, поки субтитри розгорнуті, але не в компактному вигляді. Розширення для браузера показує читання на бічній панелі, але не в субтитрах, які воно малює поверх сторінки зустрічі."
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "Перемикач з’являється лише тоді, коли в цільової мови є читання: китайська (спрощена), японська, корейська, російська або українська. У традиційної китайської та кантонської їх поки немає.",
      "Якщо цільовий напрямок двосторонній, наприклад китайська ⇄ англійська, читання отримує китайська половина.",
      "Поки рядок ще вимовляється, читання його останнього слова з’являється, коли рядок закінчиться.",
      "Для китайських, корейських, російських та українських читань завантаження не потрібне."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Чому японське читання буває хибним?",
        "answer": "Словник дає кожному слову одне читання і не може за змістом зрозуміти, яке мається на увазі. 空いている читається あいている (вільно) навіть у 電車が空いている, де мається на увазі すいている (не людно), а 辛い читається つらい (важко) навіть для гострого карі, де мається на увазі からい. Числа з лічильними словами (一人, 3日, 4月1日) і お腹が空いた обробляються. Слово новіше за словник, наприклад 令和, також може бути прочитане хибно."
      },
      {
        "question": "Чи можна отримати ромадзі для японської?",
        "answer": "Поки ні. Наразі для японської показується кана."
      },
      {
        "question": "Чи потрібен для читань інтернет?",
        "answer": "Читання обчислюються на вашому комп’ютері. Завантажити потрібно лише японський словник, і лише один раз."
      },
      {
        "question": "Чи займають вони багато пам’яті?",
        "answer": "Японські читання займають близько 170 MB, поки ввімкнені, а коли перемикач вимкнено, пам’ять звільняється. Інші мови займають дуже мало."
      },
      {
        "question": "Чи видаляється словник, якщо очистити завантажені моделі?",
        "answer": "Так. Він завантажиться знову, коли наступного разу ввімкнуть японські читання."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "Letture di pronuncia: vedere come si pronuncia l’altra lingua",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "Le letture mostrano come si pronuncia la lingua dell’altra parte, in piccolo sopra le parole: il pinyin sul cinese, i kana sui kanji giapponesi, la romanizzazione sul coreano e le lettere latine sul russo e sull’ucraino. Aiutano quando leggi ad alta voce una traduzione, o segui l’altra parte, senza sapere come suonano le parole. Le letture sono solo disegnate sullo schermo: non vengono mai inviate al provider di IA, e la trascrizione, le esportazioni e il testo copiato restano come prima."
  },
  "steps": [
    {
      "title": "Attivare le letture",
      "content": "Fai clic sull’ingranaggio nella barra degli strumenti sopra la conversazione per aprire le impostazioni di visualizzazione. Quando la lingua di destinazione ha le letture, il pannello ha un interruttore che la nomina, qui <strong>Mostra le letture in giapponese (kana)</strong>. Attivalo.",
      "screenshot": "01-switch.png"
    },
    {
      "title": "Quali righe hanno le letture",
      "content": "Le letture compaiono su ogni riga nella lingua di destinazione: le tue traduzioni e le parole dell’altra parte quando parla in quella lingua. Le righe in altre lingue restano come sono. Qui il cinese viene tradotto in giapponese: le mie traduzioni hanno i kana, e anche ciò che l’altra parte dice in giapponese. In una conversazione faccia a faccia, ogni riga segue la propria lingua.",
      "screenshot": "02-japanese.png"
    },
    {
      "title": "Cinese: pinyin",
      "content": "Ogni carattere riceve il suo pinyin, con i segni dei toni. Un carattere con più di una lettura viene letto in base al contesto (还 hái, 重新 chóng, 银行 háng), e i cambi di tono di 一 e 不 sono mostrati come vengono pronunciati (一趟 yí tàng).",
      "screenshot": "03-chinese.png"
    },
    {
      "title": "Coreano: romanizzazione",
      "content": "Ogni parola riceve la sua Romanizzazione riveduta, scritta come suona la parola e non lettera per lettera: 같이 gachi, 국물이 gungmuri, 신라호텔 sillahotel.",
      "screenshot": "04-korean.png"
    },
    {
      "title": "Russo e ucraino: lettere latine",
      "content": "Ogni parola è scritta in lettere latine: il russo con il sistema BGN/PCGN (Здравствуйте zdravstvuyte, всё vsyo), l’ucraino con il sistema nazionale del 2010 (Київ kyiv). Le lettere seguono l’ortografia, che non sempre corrisponde al suono di una parola.",
      "screenshot": "05-russian.png"
    },
    {
      "title": "Giapponese: kana e dizionario",
      "content": "I kana vanno solo sopra i kanji; hiragana e katakana restano come sono. Le letture in giapponese richiedono un dizionario da 17 MB, che viene scaricato la prima volta che le attivi e conservato per dopo. Il pannello mostra il download mentre è in corso. Se fallisce, o se le letture in giapponese smettono di funzionare, il pannello lo segnala e propone <strong>Riprova</strong>.",
      "screenshot": "06-download.png"
    },
    {
      "title": "Nella vista sottotitoli",
      "content": "Nell’app desktop, la vista sottotitoli ha le proprie impostazioni di visualizzazione, con un interruttore delle letture suo, indipendente da quello della conversazione. Le letture compaiono quando i sottotitoli sono espansi, non nella vista compatta. L’estensione del browser mostra le letture nel pannello laterale, non nei sottotitoli che disegna sopra la pagina della riunione."
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "L’interruttore compare solo quando la lingua di destinazione ha le letture: cinese (semplificato), giapponese, coreano, russo o ucraino. Il cinese tradizionale e il cantonese non ne hanno ancora.",
      "Con una destinazione bidirezionale come cinese ⇄ inglese, la metà cinese riceve le letture.",
      "Mentre una riga è ancora in corso, la sua ultima parola riceve la lettura quando la riga è conclusa.",
      "Le letture di cinese, coreano, russo e ucraino non richiedono alcun download."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "Perché una lettura in giapponese è sbagliata?",
        "answer": "Il dizionario dà a ogni parola una sola lettura e non può capire dal significato quale sia quella giusta. 空いている si legge あいている (libero) anche in 電車が空いている, dove si intende すいている (poco affollato), e 辛い si legge つらい (doloroso) anche per un curry piccante, dove si intende からい. I numeri con i contatori (一人, 3日, 4月1日) e お腹が空いた sono gestiti. Anche una parola più recente del dizionario, come 令和, può essere letta male."
      },
      {
        "question": "Posso avere il romaji per il giapponese?",
        "answer": "Non ancora. Per ora il giapponese mostra i kana."
      },
      {
        "question": "Le letture richiedono internet?",
        "answer": "Le letture vengono calcolate sul tuo computer. L’unico download è il dizionario giapponese, una sola volta."
      },
      {
        "question": "Usano molta memoria?",
        "answer": "Le letture in giapponese usano circa 170 MB finché sono attive, e disattivando l’interruttore la memoria viene liberata. Le altre lingue ne usano pochissima."
      },
      {
        "question": "Cancellare i modelli scaricati rimuove il dizionario?",
        "answer": "Sì. Viene scaricato di nuovo la prossima volta che attivi le letture in giapponese."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function FeaturePronunciationReadings() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/pronunciation-readings" />;
}
